// src/components/ProductList/ProductList.js
//
// Thin drawer wrapper around <ProductListContent />.
// All presentational and business logic lives in ProductListContent.js;
// this file only adds the bottom-sheet drawer chrome (portal, overlay,
// swipe-to-close container).

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useSwipeToClose from "../../hooks/useSwipeToClose";
import ProductListContent from "./ProductListContent";

const ProductList = ({
  products,
  onClose,
  maxStorageTime,
  loading,
  processingStatus,
  leftovers,
  country = "Spain",
  shoppingMode = false,
  isAdmin = false,
}) => {
  const {
    containerRef,
    dragOffset,
    isAnimating,
    shouldRender,
    isVisible,
    isDragging,
    DragHandle,
  } = useSwipeToClose(onClose, true, { fixedScrollLock: false });

  const portalContainerRef = useRef(null);

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "product-list-portal";
    container.style.position = "relative";
    container.style.zIndex = "99998";
    document.body.appendChild(container);
    portalContainerRef.current = container;

    return () => {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, []);

  if (!shouldRender || !portalContainerRef.current) return null;

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 bg-[#0D0C07] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition:
          "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        zIndex: 99998,
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content h-[95dvh] max-h-[95dvh]"
        style={{
          transform: isVisible
            ? `translate3d(0, ${Math.max(0, dragOffset)}px, 0)`
            : "translate3d(0, 100%, 0)",
          transition:
            (dragOffset === 0 && isAnimating) ||
            (!isDragging && dragOffset === 0)
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        <ProductListContent
          variant="drawer"
          onRequestClose={onClose}
          products={products}
          maxStorageTime={maxStorageTime}
          loading={loading}
          processingStatus={processingStatus}
          leftovers={leftovers}
          country={country}
          shoppingMode={shoppingMode}
          isAdmin={isAdmin}
        />
      </div>
    </div>,
    portalContainerRef.current
  );
};

export default ProductList;
